import React, { useState } from "react";

/**
 * NumberInput component that provides a number input field with validation
 * Features debounced updates to prevent excessive API calls during typing
 *
 * @param {Object} props - Component props
 * @param {Object} props.field - Field configuration object with validation rules (min, max, unit)
 * @param {Object} props.properties - Current form properties/values
 * @param {string} props.property - The property key this input manages
 * @param {Function} props.updateProperties - Callback to update parent component properties
 * @param {number} props.step - Step value for number increment/decrement (default: 1)
 * @param {boolean} props.needsvalidation - Whether to show validation feedback (default: false)
 * @param {Function} props.onFocus - Custom focus handler (optional)
 * @param {Function} props.onBlur - Custom blur handler (optional)
 */
const NumberInput = ({
    field,
    properties,
    property,
    updateProperties,
    step = 1,
    needsvalidation = false,
    onFocus,
    onBlur,
}) => {
    // State to store the typing timeout ID for debouncing
    const [typingTimeout, setTimingTimeout] = useState(0);

    /**
     * Handle input value changes with debouncing
     * Immediately updates local state, then waits 500ms before triggering parent update
     *
     * @param {Event} e - The input change event
     */
    function updateValue(e) {
        // Clone properties and update the specific property
        let props = { ...properties };
        props[property] = parseInt(e.target.value);

        // Update immediately without triggering side effects (false parameter)
        updateProperties(props, false);

        // Clear existing timeout to restart debounce timer
        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }

        // Set new timeout for delayed update with side effects
        setTimingTimeout(
            setTimeout(() => {
                updateProperties(props); // Trigger full update after delay
            }, 500),
        );
    }

    /**
     * Handle input focus event by selecting all text
     * Improves user experience by allowing quick value replacement
     *
     * @param {Event} event - The focus event
     */
    function handleFocus(event) {
        if (event) {
            event.target.select();
        }
        // Call custom focus handler if provided
        if (onFocus) {
            onFocus(event);
        }
    }

    /**
     * Handle input blur event
     *
     * @param {Event} event - The blur event
     */
    function handleBlur(event) {
        // Call custom blur handler if provided
        if (onBlur) {
            onBlur(event);
        }
    }

    return (
        <div className={"row forminput"}>
            <div
                className={`col-sm-12 needs-validation ${needsvalidation && "was-validated"}`}
            >
                <div className="input-group input-group-number has-validation">
                    <input
                        type="number"
                        className="form-control"
                        name={property}
                        onChange={updateValue}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        step={step}
                        value={properties[property] || ""}
                        min={field.min} // Minimum allowed value from field config
                        max={field.max} // Maximum allowed value from field config
                        disabled={field.disabled} // Disable input when field is disabled
                    />

                    {/* Optional unit display (e.g., "mm", "cm", "kg") */}
                    {field.unit ? (
                        <div className="input-group-append">
                            <span className="input-group-text">
                                {field.unit}
                            </span>
                        </div>
                    ) : (
                        <></>
                    )}

                    {/* Validation feedback message */}
                    <div className="invalid-feedback">
                        De waarde van dit veld is niet juist
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NumberInput;
